import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  useLogin,
  useMagicLink,
  useOtp,
  useWebAuthn,
  isWebAuthnSupported,
} from '@theraply/api-client';
import { Button, Input } from '@theraply/ui';

type AuthMethod = 'password' | 'magic-link' | 'otp' | 'passkey';

export default function LoginPage() {
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password');
  const [error, setError] = useState<string | null>(null);

  // Password auth state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Magic link state
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // OTP state
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  // Hooks
  const { mutate: login, isPending: isLoginPending } = useLogin();
  const { request: magicLinkRequest, verify: magicLinkVerify } = useMagicLink();
  const { request: otpRequest, verify: otpVerify } = useOtp();
  const { login: webAuthnLogin, isSupported: webAuthnSupported } = useWebAuthn();

  const handlePasswordLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    login(
      { email, password, userType: 'therapist' },
      {
        onSuccess: () => navigate('/'),
        onError: (err) => setError(err.message || 'Invalid email or password'),
      }
    );
  };

  const handleMagicLinkRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    magicLinkRequest.mutate(
      { email: magicLinkEmail, userType: 'therapist' },
      {
        onSuccess: () => setMagicLinkSent(true),
        onError: (err) => setError(err.message || 'Failed to send magic link'),
      }
    );
  };

  const handleOtpRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    otpRequest.mutate(
      { phone, userType: 'therapist' },
      {
        onSuccess: () => setOtpSent(true),
        onError: (err) => setError(err.message || 'Failed to send OTP'),
      }
    );
  };

  const handleOtpVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    otpVerify.mutate(
      { phone, code: otpCode, userType: 'therapist' },
      {
        onSuccess: () => navigate('/'),
        onError: (err) => setError(err.message || 'Invalid OTP code'),
      }
    );
  };

  const handlePasskeyLogin = () => {
    setError(null);
    webAuthnLogin.mutate(
      { userType: 'therapist' },
      {
        onSuccess: () => navigate('/'),
        onError: (err) => setError(err.message || 'Passkey authentication failed'),
      }
    );
  };

  const tabs = [
    { id: 'password' as const, label: 'Password' },
    { id: 'magic-link' as const, label: 'Magic Link' },
    { id: 'otp' as const, label: 'SMS Code' },
    ...(webAuthnSupported ? [{ id: 'passkey' as const, label: 'Passkey' }] : []),
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-4xl font-bold text-primary-600">Theraply</h1>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Therapist Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to access your therapist dashboard
          </p>
        </div>

        {/* Auth method tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setAuthMethod(tab.id);
                setError(null);
              }}
              className={`flex-1 py-3 px-4 text-sm font-medium text-center border-b-2 transition-colors ${
                authMethod === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Password authentication */}
        {authMethod === 'password' && (
          <form className="space-y-6" onSubmit={handlePasswordLogin}>
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>
            <Button type="submit" fullWidth isLoading={isLoginPending}>
              Sign in
            </Button>
          </form>
        )}

        {/* Magic link authentication */}
        {authMethod === 'magic-link' && (
          <div className="space-y-6">
            {!magicLinkSent ? (
              <form onSubmit={handleMagicLinkRequest} className="space-y-6">
                <p className="text-sm text-gray-600">
                  Enter your email and we will send you a secure link to sign in.
                </p>
                <Input
                  id="magic-link-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  label="Email address"
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  fullWidth
                />
                <Button type="submit" fullWidth isLoading={magicLinkRequest.isPending}>
                  Send Magic Link
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-green-800">
                    Check your email! We have sent a magic link to{' '}
                    <strong>{magicLinkEmail}</strong>.
                  </p>
                  <p className="text-sm text-green-700 mt-2">
                    The link will expire in 15 minutes.
                  </p>
                </div>
                <button
                  onClick={() => setMagicLinkSent(false)}
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  Did not receive it? Send again
                </button>
              </div>
            )}
          </div>
        )}

        {/* OTP authentication */}
        {authMethod === 'otp' && (
          <div className="space-y-6">
            {!otpSent ? (
              <form onSubmit={handleOtpRequest} className="space-y-6">
                <p className="text-sm text-gray-600">
                  Enter your phone number and we will send you a verification code.
                </p>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  label="Phone number"
                  placeholder="+1 (555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  fullWidth
                />
                <Button type="submit" fullWidth isLoading={otpRequest.isPending}>
                  Send Code
                </Button>
              </form>
            ) : (
              <form onSubmit={handleOtpVerify} className="space-y-6">
                <p className="text-sm text-gray-600">
                  Enter the 6-digit code sent to <strong>{phone}</strong>
                </p>
                <Input
                  id="otp-code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  label="Verification code"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  fullWidth
                />
                <Button type="submit" fullWidth isLoading={otpVerify.isPending}>
                  Verify Code
                </Button>
                <button
                  type="button"
                  onClick={() => setOtpSent(false)}
                  className="w-full text-sm text-primary-600 hover:text-primary-500"
                >
                  Use a different number
                </button>
              </form>
            )}
          </div>
        )}

        {/* Passkey authentication */}
        {authMethod === 'passkey' && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              Use your device biometrics (Face ID, Touch ID, Windows Hello) or security
              key to sign in securely.
            </p>
            <Button
              onClick={handlePasskeyLogin}
              fullWidth
              isLoading={webAuthnLogin.isPending}
            >
              Sign in with Passkey
            </Button>
          </div>
        )}

        {/* Register link */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Don&apos;t have an account?{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Register as a therapist
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
